import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type { BadgeTone } from "@empite/ui-components";
import type { Reservation, SettlementFailure, SettlementFailureKind, TopUp } from "../admin-client";
import { useReservations, useSettlementFailures, useTopUps } from "../admin-hooks";
import { ErrorAlert, RequireOperator } from "./guards";
import { Money, ShortID, StatusBadge, when } from "./common";

/**
 * Payments — the three lists an operator opens when money has not arrived
 * where somebody expected it.
 *
 * They are one screen because the customer's question is one question. "I
 * paid and my balance did not change" can be a top-up the provider never
 * completed, a hold nothing ever released, a webhook the payments worker
 * could not apply, a usage report the settlement worker choked on, or a job
 * that died after five attempts — five different tables, and the operator
 * does not know which one before they look.
 */
const PAGE = 100;

const TOP_UP_STATUSES = [
  "pending",
  "processing",
  "succeeded",
  "failed",
  "cancelled",
  "refunded",
];

export interface PaymentsSearch {
  status?: string;
}

export function Payments({ search }: { search: PaymentsSearch }) {
  return <RequireOperator>{() => <PaymentsPage search={search} />}</RequireOperator>;
}

function PaymentsPage({ search }: { search: PaymentsSearch }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(search.status ?? "unsettled");

  useEffect(() => {
    setStatus(search.status ?? "unsettled");
  }, [search.status]);

  // "unsettled" is not a status the platform stores — it is the server's own
  // filter for everything that took money's shape and never became money.
  // Defaulting to it is the point of the screen: an operator arriving here is
  // looking for what went wrong, not for a payment history.
  const chosen = search.status ?? "unsettled";
  const topUps = useTopUps({
    status: chosen === "unsettled" ? undefined : chosen,
    unsettled: chosen === "unsettled",
    limit: PAGE,
  });
  const failures = useSettlementFailures(PAGE);
  // Only the held-past-expiry ones. A screen listing every reservation ever
  // taken would be an inference log with a money column; what an operator
  // needs here is the short list of holds the sweep has not released.
  const stale = useReservations({ stale: true, limit: PAGE });

  return (
    <>
      <PageHeader
        title="Payments & settlement"
        description="Top-ups that did not land, funds held past their expiry, and the four places a settlement can stop moving."
      />

      <Card title="Top-ups">
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              to: "/payments",
              search: { status: status === "unsettled" ? undefined : status },
            });
          }}
        >
          <Field label="Status" htmlFor="topup-status">
            <Select
              id="topup-status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="unsettled">Not settled (pending, processing, failed, cancelled)</option>
              {TOP_UP_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" busy={topUps.isFetching}>
            Apply
          </Button>
        </form>
        <ErrorAlert error={topUps.error} title="Could not load the top-ups" />
        {topUps.isPending ? (
          <Spinner label="Loading top-ups" />
        ) : (
          <DataTable<TopUp>
            rows={topUps.data?.top_ups ?? []}
            rowKey={(row) => row.id}
            empty="No top-up matches that filter — nothing is stuck."
            columns={[
              {
                key: "status",
                header: "Status",
                render: (row) => (
                  <>
                    <StatusBadge status={row.status} />
                    {/* Money in flight at the provider with nothing posted.
                        A `succeeded` row cannot be in this state — a database
                        constraint requires it to carry a ledger transaction. */}
                    {row.paid_not_credited ? <Badge tone="warning">not credited yet</Badge> : null}
                  </>
                ),
              },
              {
                key: "owner",
                header: "Customer",
                render: (row) => (
                  <>
                    <Link to="/wallets/$walletId" params={{ walletId: row.wallet_id }}>
                      {row.owner_label || row.wallet_id}
                    </Link>
                    <div className="esm-muted-text">
                      <code>{row.application_slug}</code>
                    </div>
                  </>
                ),
              },
              {
                key: "amount",
                header: "Amount",
                align: "end",
                render: (row) => <Money value={row.amount} />,
              },
              {
                key: "provider",
                header: "Provider reference",
                // The intent id, not the checkout URL. The Admin API does not
                // send a checkout URL and this screen has nowhere to put one:
                // it is the customer's payment page.
                render: (row) => (
                  <>
                    <code>{row.provider}</code>
                    <div className="esm-muted-text">
                      <ShortID value={row.provider_payment_intent_id} />
                    </div>
                  </>
                ),
              },
              {
                key: "posted",
                header: "Ledger",
                render: (row) => <ShortID value={row.ledger_transaction_id} />,
              },
              {
                key: "failure",
                header: "Why",
                render: (row) =>
                  row.failure_reason ? row.failure_reason : <span className="esm-muted-text">—</span>,
              },
              { key: "created", header: "Started", render: (row) => when(row.created_at) },
            ]}
          />
        )}
      </Card>

      <Card title="Funds held past their expiry">
        <Alert tone="info" title="Held, expired, and neither charged nor returned">
          A reservation is taken before the provider is dispatched and released or settled when the
          request ends. One still held after its expiry is money the customer cannot spend and
          nobody is charging for — it means the stale-reservation sweep has not reached it.
        </Alert>
        <ErrorAlert error={stale.error} title="Could not load the held reservations" />
        {stale.isPending ? (
          <Spinner label="Looking for stale holds" />
        ) : (
          <DataTable<Reservation>
            rows={stale.data?.reservations ?? []}
            rowKey={(row) => row.id}
            empty="No reservation is held past its expiry. Every hold has been settled or released."
            columns={[
              {
                key: "wallet",
                header: "Wallet",
                render: (row) => (
                  <>
                    <Link to="/wallets/$walletId" params={{ walletId: row.wallet_id }}>
                      <ShortID value={row.wallet_id} />
                    </Link>
                    <div className="esm-muted-text">
                      <code>{row.application_slug}</code>
                    </div>
                  </>
                ),
              },
              {
                key: "reserved",
                header: "Held",
                align: "end",
                render: (row) => <Money value={row.reserved} />,
              },
              {
                key: "request",
                header: "Inference request",
                render: (row) => <ShortID value={row.inference_request_id} />,
              },
              { key: "taken", header: "Taken", render: (row) => when(row.created_at) },
              { key: "expired", header: "Should have expired", render: (row) => when(row.expires_at) },
            ]}
          />
        )}
      </Card>

      <Card title="Where settlement stopped">
        <Alert tone="info" title="Four sources, one time order">
          A failed payment webhook, a usage report the settlement worker could not apply, an open
          reconciliation case and a dead queue job are read together and shown newest first. None of
          them is fixed from this console — they are worked by the platform's own retries and
          sweeps, and this is where you see what they have not reached.
        </Alert>
        <ErrorAlert error={failures.error} title="Could not load the settlement failures" />
        {failures.isPending ? (
          <Spinner label="Loading the backlog" />
        ) : (
          <DataTable<SettlementFailure>
            rows={failures.data?.failures ?? []}
            rowKey={(row) => `${row.kind}:${row.id}`}
            empty="Nothing is stuck. Every payment event, usage report and job has been worked."
            columns={[
              {
                key: "kind",
                header: "Source",
                render: (row) => <Badge tone={kindTone(row.kind)}>{kindLabel(row.kind)}</Badge>,
              },
              { key: "status", header: "State", render: (row) => <code>{row.status}</code> },
              {
                key: "application",
                header: "Application",
                render: (row) =>
                  row.application_slug ? (
                    <code>{row.application_slug}</code>
                  ) : (
                    // A dead queue job knows its kind and not necessarily
                    // whose it was. Saying so is better than a blank cell that
                    // reads as a page that failed to load.
                    <span className="esm-muted-text">unattributed</span>
                  ),
              },
              {
                key: "reference",
                header: "Reference",
                render: (row) => (
                  <>
                    <code className="esm-short-id" title={row.reference}>
                      {row.reference ?? "—"}
                    </code>
                    <div className="esm-muted-text">
                      <ShortID value={row.subject} />
                    </div>
                  </>
                ),
              },
              {
                key: "attempts",
                header: "Attempts",
                align: "end",
                render: (row) =>
                  row.attempts === undefined ? (
                    <span className="esm-muted-text">—</span>
                  ) : (
                    row.attempts
                  ),
              },
              {
                key: "detail",
                header: "Detail",
                render: (row) =>
                  row.detail ? row.detail : <span className="esm-muted-text">—</span>,
              },
              { key: "occurred", header: "When", render: (row) => when(row.occurred_at) },
            ]}
          />
        )}
      </Card>
    </>
  );
}

/**
 * The four sources, coloured by how much money is at stake in each.
 *
 * A failed payment webhook means a customer has paid and has no credit, which
 * is the loudest thing on this page. A dead job might be either — its payload
 * says which — so it stays neutral rather than crying wolf.
 */
function kindTone(kind: SettlementFailureKind): BadgeTone {
  switch (kind) {
    case "payment_event":
      return "danger";
    case "usage_event":
    case "reconciliation_case":
      return "warning";
    default:
      return "neutral";
  }
}

function kindLabel(kind: SettlementFailureKind): string {
  switch (kind) {
    case "payment_event":
      return "payment webhook";
    case "usage_event":
      return "usage report";
    case "reconciliation_case":
      return "reconciliation";
    default:
      return "dead job";
  }
}
